import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ViewChild,
} from "@angular/core";

import { DsAutocompleteComponent } from "src/app/modules/form-controls/components/ds-autocomplete/ds-autocomplete.component";
import { TranslateService } from "@ngx-translate/core";

import { EmpleadoViewModel } from "src/app/_core/models/centros/empleado.model";
import {
	empleadoLabel,
	empleadoTieneNombre,
	empleadoTitleCase,
} from "src/app/_core/utils/empleado-display";

/**
 * Buscador de personas canónico (modo edición), formato `.ds`.
 * - Presentación y comportamiento (desplegable, teclado, click-fuera) delegados en
 *   `app-ds-autocomplete`; este componente aporta el sabor "persona".
 * - Búsqueda multi-campo (nombre, email, código, función), insensible a acentos y
 *   tokenizada (todos los términos deben aparecer).
 * - Avatar con foto de EntraID (fallback iniciales) en las opciones y, una vez hay
 *   selección, como prefijo del campo.
 * - Resultados acotados (rendimiento) con aviso de cuántos más hay.
 * El consumidor pasa la lista (`users`) y recibe la selección por `(userSelected)`
 * (solo selecciones reales; nunca emite null — mismo contrato de siempre).
 */
@Component({
	selector: "app-user-search",
	templateUrl: "./user-search.component.html",
	styleUrls: ["./user-search.component.scss"],
	standalone: false,
})
export class UserSearchComponent implements OnChanges {
	@Input() users: EmpleadoViewModel[] = [];
	@Input() label = "";
	@Input() placeholder = "";
	@Input() disabled = false;
	/** Selección actual (preselección al entrar en modo edición). No emite userSelected. */
	@Input() value: EmpleadoViewModel | null = null;
	/** Texto de ayuda opcional (icono info con tooltip, como los formularios de detalle). */
	@Input() tooltip = "";
	/** Selección (o `null` si el usuario limpia explícitamente con la X). */
	@Output() userSelected = new EventEmitter<EmpleadoViewModel | null>();

	/** Selección visible (espejo de `value` + selecciones del usuario). */
	current: EmpleadoViewModel | null = null;

	@ViewChild(DsAutocompleteComponent)
	private readonly dsac?: DsAutocompleteComponent;

	readonly MAX_RESULTS = 50;

	constructor(private readonly translate: TranslateService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["value"]) {
			this.current = this.value ?? null;
		}
	}

	/** Texto del campo tras seleccionar: etiqueta robusta (nombre con casing → email → código). */
	displayFn = (user?: EmpleadoViewModel): string =>
		user ? empleadoLabel(user) : "";

	/** Búsqueda tokenizada multi-campo, insensible a acentos (query llega en minúsculas). */
	filterFn = (user: EmpleadoViewModel, query: string): boolean => {
		const tokens = this._norm(query).split(/\s+/).filter(Boolean);
		if (!tokens.length) {
			return true;
		}
		const hay = this._norm(
			`${user.nombre} ${user.email} ${user.codigo} ${user.funcionAgregada ?? ""}`,
		);
		return tokens.every((t) => hay.includes(t));
	};

	/** Pie del panel cuando hay más resultados que los mostrados. */
	moreTextFn = (shown: number, total: number): string =>
		this.translate.instant("userSearch.more", { shown, total });

	onValueChange(user: EmpleadoViewModel | null): void {
		if (!user) {
			// Borrar el texto a mano no cambia la selección del consumidor:
			// se restaura la selección vigente al perder el foco (onTouched).
			// La limpieza explícita va por la X (onCleared).
			return;
		}
		this.current = user;
		this.userSelected.emit(user);
	}

	/** Limpieza explícita con la X: deselecciona de verdad (emite null). */
	onCleared(): void {
		if (!this.current) {
			return;
		}
		this.current = null;
		this.userSelected.emit(null);
	}

	/** Al perder el foco con el texto borrado, se restaura la selección vigente. */
	onTouched(): void {
		if (this.current && this.dsac && !this.dsac.value) {
			this.dsac.value = this.current;
		}
	}

	/** true si el empleado tiene nombre real (para distinguir "asignado sin nombre"). */
	hasName(user: EmpleadoViewModel): boolean {
		return empleadoTieneNombre(user);
	}

	/** Nombre con casing "bonito" cuando existe; si no, marca explícita de sin nombre. */
	name(user: EmpleadoViewModel): string {
		return empleadoTieneNombre(user)
			? empleadoTitleCase(user.nombre)
			: "Sin nombre registrado";
	}

	// Rango de marcas diacríticas combinantes (U+0300–U+036F) construido sin literales.
	private static readonly DIACRITICS = new RegExp("[\\u0300-\\u036f]", "g");

	private _norm(s: string): string {
		return (s ?? "")
			.normalize("NFD")
			.replace(UserSearchComponent.DIACRITICS, "")
			.toLowerCase();
	}
}
